import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/store/auth';
import { paths } from './paths';

interface Props {
  children: ReactNode;
}

export function RequireAdmin({ children }: Props) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const location = useLocation();

  if (!user || !token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${paths.login}?next=${next}`} replace />;
  }

  if (user.role !== 'system_admin' && user.role !== 'support') {
    // Defensive — login already gates this, but a hand-crafted token
    // should not get a working SPA.
    return <Navigate to={paths.login} replace />;
  }

  return <>{children}</>;
}

interface SystemAdminProps {
  children: ReactNode;
  fallback?: ReactNode;
}

// Routes / actions exclusive to system_admin. Support sees fallback.
export function RequireSystemAdmin({ children, fallback }: SystemAdminProps) {
  const user = useAuthStore((s) => s.user);
  if (user?.role !== 'system_admin') {
    return <>{fallback ?? <Navigate to={paths.dashboard} replace />}</>;
  }
  return <>{children}</>;
}
